import {Component, Input, OnInit, ViewChild} from '@angular/core';

import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";
import {FormArray, FormBuilder, FormGroup, Validators} from "@angular/forms";
import {ValidatorsService} from "@core/validators/validators.service";
import {ThingProvider} from "@providers/thing.provider";
import {ThresholdUtil} from "../../../util/threshold";
import {ThingsUtil} from "../../../util/things";
import { ThingConfig } from '@core/thing/thingConfig';
import {RestroomOpeningComponent} from "@components/things/restroomOpening/restroomOpening.component";
import {KngActivityIndicatorService} from "@layout/kingswell/services/kngActivityIndicator.service";
import {NotificationService} from "@core/notification/notification.service";
import { ParkService } from '@core/park/park.service';
import { ParkUtil } from '../../../util/park';

interface MyFormGroup {
  [key: string]: any;
}

@Component({
  selector: 'edit-threshold',
  templateUrl: './editThreshold.component.html',
  styleUrls: ['./editThreshold.component.less']
})

export class EditThresholdComponent implements OnInit {
  public availableStartTime = [];
  public availableEndTime = [];

  @Input()
  thing;

  public form: FormGroup;

  public config: ThingConfig;

  public restroomOpeningModel: any;
  public isTimeBased: boolean;

  private defaultThreshold: number;
  private thresholdMax: number;

  public showHeaderBottomLine: boolean = false;

  @ViewChild(RestroomOpeningComponent) restroomOpeningComponent: RestroomOpeningComponent;

  constructor(public activeModal: NgbActiveModal, private fb: FormBuilder, private validator: ValidatorsService,
              private park: ParkService, private thingProvider: ThingProvider,
              private thingUtil: ThingsUtil, private thresholdUtil: ThresholdUtil,
              private kngActivityIndicator: KngActivityIndicatorService, private notification: NotificationService,
              private parksUtil: ParkUtil) {
    this._createThresholdOptions();
  }

  _createThresholdOptions() {
    for (let i = ThresholdUtil.START_TIME; i < ThresholdUtil.END_TIME; i++) {
      this.availableStartTime.push(this._buildStartTime(i));
      this.availableEndTime.push(this._buildEndTime(i));
    }
  }

  _buildStartTime(hourBlock) {
    return {
      display: this.thresholdUtil.convertBlockToStartTime(hourBlock),
      value: hourBlock
    }
  }

  _buildEndTime(hourBlock) {
    return {
      display: this.thresholdUtil.convertBlockToEndTimeDisplay(hourBlock),
      value: hourBlock + 1
    }
  }

  ngOnInit() {
    this.park.getAreaById(this.thing.area).then(area => {
      this.config = this.thingUtil.getConfig(this.thing);
      this.isTimeBased = this.determineHasOpening();
      this.defaultThreshold = this.isTimeBased ? this.parksUtil.getAreaMinutesToNextTask(area) : this.parksUtil.getAreaThreshold(area);
      this.thresholdMax = this.isTimeBased ? 999 : 100;

      this.restroomOpeningModel = {
        openingTimeDelay: this.thing.openingTimeDelay,
        openingTime: this.thing.openingTime
      };

      this.form = this.fb.group({
        itemRows: this.fb.array(this.initRows(this.thing)) // here
      });
    });
  }

  ngAfterViewInit() {
    if( this.restroomOpeningComponent ) {
      this.form.addControl('restroomOpeningForm', this.restroomOpeningComponent.form);
      this.restroomOpeningComponent.form.setParent(this.form);
    }
  }

  determineHasOpening() {
    return (this.determineIsRestroom() || this.determineIsTimeBased());
  }

  determineIsRestroom() {
    return this.thingUtil.isRestroom(this.thing);
  }

  determineIsTimeBased() {
    return this.thingUtil.isTimeBasedTask(this.thing);
  }

  initRows(thing) {
    if (!thing)
      return [];

    let thingThresholdConfig = thing.thresholdConfig ? thing.thresholdConfig : [];

    let a = [];
    for (let t of thingThresholdConfig) {
      a.push(this.initItemRow(this.thresholdUtil.convertThresholdToUiModel(t)));
    }

    if (a.length == 0) {
      a.push(this.initItemRow({
        start: ThresholdUtil.START_TIME,
        end: ThresholdUtil.END_TIME,
        threshold: this.defaultThreshold
      }));
    }

    return a;
  }

  initItemRow(thresholdUiValue) {
    return this.fb.group({
      // list all your form controls here, which belongs to your form array
      start: thresholdUiValue.start,
      end: thresholdUiValue.end,
      threshold: [thresholdUiValue.threshold, [Validators.required, Validators.min(0), Validators.max(this.thresholdMax), this.validator.isInteger]]
    } as MyFormGroup);
  }

  _assignThresholdToThing() {
    if (this.thing) {
      this.thing.thresholdConfig = this.getModelThresholds();
    }

    return this.thing;
  }

  getModelThresholds() {
    let thresholds = this.form.controls['itemRows'] as FormArray;

    let modelThresholds = [];

    for (let tc of thresholds.controls) {
      let t: FormGroup = tc as FormGroup;
      modelThresholds.push(this.thresholdUtil.convertUiModelToThreshold({
        start: t.controls['start'].value,
        end: t.controls['end'].value,
        threshold: t.controls['threshold'].value
      }));
    }

    return modelThresholds;
  }

  submitForm(value: any): void {
    this.kngActivityIndicator.show();
    let thing = this._assignThresholdToThing();

    if (this.isTimeBased) {
      if( this.restroomOpeningModel.openingTime ){
        thing.openingTimeDelay = null;
        thing.openingTime = this.restroomOpeningModel.openingTime;
      }else if (this.restroomOpeningModel.openingTimeDelay || this.restroomOpeningModel.openingTimeDelay == 0) {
        thing.openingTimeDelay = this.floatOrNull(this.restroomOpeningModel.openingTimeDelay);
        thing.openingTime = '';
      }
    }

    this.thingProvider.updateThing(thing)
      .then(res => {
        console.log("successful save thing:" + thing.thingId);
        this.kngActivityIndicator.close();
        this.notification.show('success', 'You have successfully edited ' + (thing.name || thing.thingId) + '.');
        this.activeModal.close(thing);
      })
      .catch((err) => {
        console.error(err);
        this.kngActivityIndicator.close();
        this.notification.show('alert', 'Something went wrong');
      })

  }

  findNextIndexEndTimeGreaterThanStartTime(endHourBlock: number, fromIndex: number) {
    let row = this.form.controls['itemRows'] as FormArray;
    for (let i = fromIndex - 1; i >= 0; i--) {
      if (endHourBlock > parseInt(row.controls[i].value.start))
        return i;
    }
    return -1;
  }

  onChangeStart(i) {
    let thresholdControls = this.form.controls['itemRows'] as FormArray;
    let rowValue = thresholdControls.controls[i].value;
    // console.log("start: " + rowValue.start + ", end:" + rowValue.end);
    let startBlock: number = parseInt(rowValue.start);

    if (startBlock == ThresholdUtil.START_TIME) {
      // If start time is selected, clear everything before the current index
      // console.log("removing records between index 0 and " + i);
      thresholdControls.controls.splice(0, i);
    } else {
      let nextIndex = this.findNextIndexEndTimeGreaterThanStartTime(startBlock, i);
      // console.log("startBlock: " + startBlock + ",currentIndex: " + i + ",nextIndex: " + nextIndex);

      if (nextIndex == -1) {
        // insert new row at the beginning
        let newValue = {
          start: ThresholdUtil.START_TIME,
          end: startBlock,
          threshold: this.defaultThreshold,
        };

        thresholdControls.insert(0, this.initItemRow(newValue))
      } else {
        let newFormGroup = thresholdControls.controls[nextIndex] as FormGroup;
        // console.log("[" + nextIndex + "] Start " + newFormGroup.controls['start'].value + " - End:" + newFormGroup.controls['end'].value);

        if (startBlock <= parseInt(newFormGroup.controls['end'].value)) {
          // set the new block's end time value
          newFormGroup.controls['end'].setValue(startBlock);

          if ((i - nextIndex) > 1) {
            // if the difference is between range is more than one,  start splicing the controls
            // console.log("splicing " + (nextIndex+1) + "," + (i - nextIndex - 1));
            thresholdControls.controls.splice(nextIndex + 1, (i - nextIndex - 1));
          }
        } else {
          // there is a gap, insert new row with start time as next time of current row, and end time as current start time
          let newValue = {
            start: newFormGroup.controls['end'].value,
            end: startBlock,
            threshold: this.defaultThreshold,
          };
          // console.log('inserting at ' + nextIndex + 1);
          thresholdControls.insert(nextIndex + 1, this.initItemRow(newValue))
        }
      }
    }
  }

  findNextIndexStartTimeLessThanEndTime(startHourBlock: number, fromIndex) {
    let row = this.form.controls['itemRows'] as FormArray;
    for (let i = fromIndex + 1; i < row.controls.length; i++) {
      if (startHourBlock < parseInt(row.controls[i].value.end))
        return i;
    }
    return -1;
  }


  onChangeEnd(i) {
    let thresholdControls = this.form.controls['itemRows'] as FormArray;
    let rowValue = thresholdControls.controls[i].value;
    // console.log("start: " + rowValue.start + ", end:" + rowValue.end);
    let endBlock: number = parseInt(rowValue.end);

    if (endBlock == ThresholdUtil.END_TIME) {
      // If end time is selected, clear everything between the current index and the end
      // console.log("removing records after: " + (i + 1));
      thresholdControls.controls.splice(i + 1, thresholdControls.controls.length - 1);
    } else {
      let nextIndex = this.findNextIndexStartTimeLessThanEndTime(endBlock, i);
      // console.log("endBlock: " + endBlock + ",currentIndex: " + i + ",nextIndex: " + nextIndex);

      if (nextIndex == -1) {
        // insert new row at the end
        let newValue = {
          start: endBlock,
          end: ThresholdUtil.END_TIME,
          threshold: this.defaultThreshold,
        };

        thresholdControls.push(this.initItemRow(newValue))
      } else {
        let newFormGroup = thresholdControls.controls[nextIndex] as FormGroup;
        // console.log("[" + nextIndex + "] Start " + newFormGroup.controls['start'].value + " - End:" + newFormGroup.controls['end'].value);

        if (endBlock >= parseInt(newFormGroup.controls['start'].value)) {
          // set the found index row's start time value
          newFormGroup.controls['start'].setValue(endBlock);

          if ((nextIndex - i) > 1) {
            // if the difference is between range is more than one,  start splicing the controls
            // console.log("splicing " + (i+1) + "," + (nextIndex - i));
            thresholdControls.controls.splice(i + 1, (nextIndex - i - 1));
          }
        } else {
          // there is a gap, insert new row with end time as start time of current row, and end time as current start time
          let newValue = {
            start: endBlock,
            end: newFormGroup.controls['start'].value,
            threshold: this.defaultThreshold,
          };

          thresholdControls.insert(nextIndex, this.initItemRow(newValue))
        }
      }
    }
  }

  onContentScroll(scrollTop) {
    this.showHeaderBottomLine = (scrollTop > 0);
  }

  floatOrNull(val) {
    let newVal = parseFloat(val);
    return isNaN(newVal) ? null : newVal;
  }
}



// WEBPACK FOOTER //
// D:/dclean-web-latest/cot-web/src/app/components/things/threshold/editThreshold.component.ts